import React, { useEffect, useState } from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, CreditCard as CardIcon, ShieldCheck, Landmark, FileCheck2 } from "lucide-react";

const BRAND_LABEL = {
  VISA: "Visa", MASTERCARD: "Mastercard", AMERICAN_EXPRESS: "Amex",
  DISCOVER: "Discover", JCB: "JCB", UNIONPAY: "UnionPay", CHINA_UNIONPAY: "UnionPay",
  DINERS_CLUB: "Diners", OTHER_BRAND: "Card",
};

/** Payment methods for a corporate account — chargeable Square card(s) on
 *  file + bank / check reference info. Rendered inside the corporate
 *  account drawer for BOTH dispatchers and corporate members. */
export default function CorpPaymentMethods({ account, canManage }) {
  const [data, setData] = useState(null);          // {cards, payment_info}
  const [methods, setMethods] = useState(null);    // Square app config
  const [addingCard, setAddingCard] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/corporate/accounts/${account.account_id}/payment-methods`);
      setData(r.data);
    } catch (e) {
      setData({ cards: [], payment_info: {} });
    }
  };

  useEffect(() => {
    api.get("/payments/methods").then(r => setMethods(r.data)).catch(() => setMethods({}));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.account_id]);

  const handleSquareToken = async (token, verifiedBuyer) => {
    setBusy(true);
    try {
      await api.post(`/corporate/accounts/${account.account_id}/cards`, {
        source_id: token.token,
        verification_token: verifiedBuyer?.token,
      });
      toast.success("Corporate card saved — consolidated invoices can now be charged to it");
      setAddingCard(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save card");
    } finally { setBusy(false); }
  };

  const removeCard = async (saved_card_id) => {
    if (!window.confirm("Remove this corporate card?")) return;
    try {
      await api.delete(`/corporate/accounts/${account.account_id}/cards/${saved_card_id}`);
      toast.success("Card removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove card");
    }
  };

  if (!data) return <div><div className="overline mb-3">Payment methods</div><div className="text-xs text-[#71717A]">Loading…</div></div>;

  const info = data.payment_info || {};
  const hasBank = info.bank_name || info.bank_account_last4;
  const hasCheck = info.check_payee || info.check_memo;
  const cardEnabled = methods?.card?.enabled;

  return (
    <div data-testid="corp-payment-methods">
      <div className="overline mb-3">Payment methods</div>

      {/* ── Cards on file (chargeable via Square) ── */}
      <div className="space-y-2 mb-3">
        {(data.cards || []).map(c => (
          <div key={c.saved_card_id} data-testid={`corp-card-${c.saved_card_id}`}
               className="border border-white/10 bg-black/30 p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-7 bg-[#D4AF37] text-[#0a0a0a] flex items-center justify-center text-[9px] font-bold tracking-wider">
                {BRAND_LABEL[c.brand] || "CARD"}
              </div>
              <div>
                <div className="text-[#F5F2EB] text-sm tracking-[0.15em]">•••• {c.last4}</div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]">
                  {c.cardholder_name || "On file"} · exp {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)} · chargeable
                </div>
              </div>
            </div>
            {canManage && (
              <button data-testid={`corp-card-delete-${c.saved_card_id}`} onClick={() => removeCard(c.saved_card_id)}
                      className="text-[#71717A] hover:text-red-400 transition-colors p-1.5" aria-label="Remove card">
                <Trash2 size={14}/>
              </button>
            )}
          </div>
        ))}
        {(data.cards || []).length === 0 && !addingCard && (
          <div className="text-xs text-[#71717A] flex items-center gap-2"><CardIcon size={13}/>No card on file yet.</div>
        )}
      </div>

      {canManage && !addingCard && (
        <button data-testid="corp-add-card-btn" onClick={() => setAddingCard(true)} className="btn-ghost !py-2 !px-3 text-[10px] mb-4">
          <Plus size={12} className="inline mr-1.5"/>Add card
        </button>
      )}

      {addingCard && cardEnabled && (
        <div className="border border-white/10 bg-black/30 p-4 mb-4" data-testid="corp-add-card-form">
          <div className="flex items-center gap-2 text-[11px] text-[#A1A1AA] mb-3">
            <ShieldCheck size={13} className="text-[#D4AF37]"/>
            Secured by Square · card details never touch our servers
          </div>
          <PaymentForm
            applicationId={methods.card.app_id}
            locationId={methods.card.location_id}
            cardTokenizeResponseReceived={handleSquareToken}
            createVerificationDetails={() => ({
              amount: "0.00", currencyCode: "USD", intent: "STORE", billingContact: {},
            })}
          >
            <CreditCard buttonProps={{ css: {
              backgroundColor: "#D4AF37", color: "#050505", fontFamily: "Outfit, sans-serif",
              fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", fontSize: "12px",
              "&:hover": { backgroundColor: "#C29B2E" },
            }}}>
              {busy ? "Saving…" : "Save corporate card"}
            </CreditCard>
          </PaymentForm>
          <button data-testid="corp-cancel-add-card" onClick={() => setAddingCard(false)}
                  className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#71717A] hover:text-[#F5F2EB]">
            Cancel
          </button>
        </div>
      )}
      {addingCard && !cardEnabled && (
        <div className="border border-white/10 p-4 mb-4 text-xs text-[#A1A1AA]">Card storage isn&apos;t configured. Please contact support.</div>
      )}

      {/* ── Bank / check on record (reference only) ── */}
      {!editingBank ? (
        <div className="border border-white/10 bg-black/30 p-4" data-testid="corp-bank-check-view">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Bank / check on record</div>
            {canManage && (
              <button data-testid="corp-edit-bank-btn" onClick={() => setEditingBank(true)}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline">
                {(hasBank || hasCheck) ? "Edit" : "Add"}
              </button>
            )}
          </div>
          {!hasBank && !hasCheck ? (
            <div className="text-xs text-[#71717A]">No bank or check details on record.</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {hasBank && (
                <>
                  <div>
                    <div className="text-[#71717A] text-[10px] uppercase tracking-[0.15em] flex items-center gap-1"><Landmark size={11}/>Bank</div>
                    <div className="text-[#F5F2EB]" data-testid="corp-bank-name">{info.bank_name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[#71717A] text-[10px] uppercase tracking-[0.15em]">Routing · Account</div>
                    <div className="text-[#F5F2EB] font-mono text-xs" data-testid="corp-bank-numbers">
                      {info.bank_routing || "—"} · {info.bank_account || (info.bank_account_last4 ? `••••${info.bank_account_last4}` : "—")}
                    </div>
                  </div>
                </>
              )}
              {hasCheck && (
                <>
                  <div>
                    <div className="text-[#71717A] text-[10px] uppercase tracking-[0.15em] flex items-center gap-1"><FileCheck2 size={11}/>Checks payable to</div>
                    <div className="text-[#F5F2EB]" data-testid="corp-check-payee">{info.check_payee || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[#71717A] text-[10px] uppercase tracking-[0.15em]">Check memo / notes</div>
                    <div className="text-[#F5F2EB] text-xs" data-testid="corp-check-memo">{info.check_memo || "—"}</div>
                  </div>
                </>
              )}
            </div>
          )}
          <div className="text-[10px] text-[#71717A] mt-3">Reference only — record check / bank payments on the invoice when received.</div>
        </div>
      ) : (
        <BankCheckForm account={account} info={info} onDone={(fresh) => { setEditingBank(false); if (fresh) setData(d => ({ ...d, payment_info: fresh })); }}/>
      )}
    </div>
  );
}

function BankCheckForm({ account, info, onDone }) {
  const [f, setF] = useState({
    bank_name: info.bank_name || "",
    bank_routing: info.bank_routing || "",
    bank_account: (info.bank_account || "").replace(/•/g, ""),
    check_payee: info.check_payee || "",
    check_memo: info.check_memo || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/corporate/accounts/${account.account_id}/payment-info`, f);
      toast.success("Payment details saved");
      onDone(r.data.payment_info);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#D4AF37]/30 bg-black/30 p-4 space-y-3" data-testid="corp-bank-check-form">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Bank / check details</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <div className="field-label">Bank name</div>
          <input data-testid="corp-bank-name-input" className="field-input" value={f.bank_name} onChange={set("bank_name")} placeholder="e.g. Chase, US Bank"/>
        </div>
        <div>
          <div className="field-label">Routing number (9 digits)</div>
          <input data-testid="corp-bank-routing-input" className="field-input font-mono" inputMode="numeric" maxLength={9} value={f.bank_routing} onChange={set("bank_routing")} placeholder="075000000"/>
        </div>
        <div>
          <div className="field-label">Account number</div>
          <input data-testid="corp-bank-account-input" className="field-input font-mono" inputMode="numeric" maxLength={17} value={f.bank_account} onChange={set("bank_account")} placeholder="000123456789"/>
        </div>
        <div>
          <div className="field-label">Checks payable to</div>
          <input data-testid="corp-check-payee-input" className="field-input" value={f.check_payee} onChange={set("check_payee")} placeholder="Delux Limo Trans Group LLC"/>
        </div>
        <div>
          <div className="field-label">Check memo / notes</div>
          <input data-testid="corp-check-memo-input" className="field-input" value={f.check_memo} onChange={set("check_memo")} placeholder="e.g. mails checks NET-30"/>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button data-testid="corp-bank-cancel" onClick={() => onDone(null)} className="btn-ghost !py-2 !px-3 text-[10px]">Cancel</button>
        <button data-testid="corp-bank-save" onClick={save} disabled={saving} className="btn-gold !py-2 !px-3 text-[10px]">{saving ? "Saving…" : "Save details"}</button>
      </div>
    </div>
  );
}
